import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';
import { PropTypes } from 'prop-types';
import { Tabs, Row } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ActionCenter from './ActionCenter';
import TabsSettings from './Tabs';
import JobCosting from './JobCosting';
import Subtasks from './Subtasks';
import ScheduleOfValuesTemplates from './ScheduleOfValuesTemplates';
import ProjectFileTemplates from './ProjectFileTemplates';
import Permissions from '../../auth/Permissions';
import Boards from './Boards';
import ProfileNames from './ProfileNames';
import TypesTabView from './TypesTabView';
import CustomFields from './CustomFields';
import BucketTemplateTabView from './BucketTemplateTabView';
import ScheduleCategories from './ScheduleCategories';
import ScheduleDefaultDateTime from './ScheduleDefaultDateTime';
import CostcodeCategories from './CostcodeCategories';
import ProjectBillingRateTemplate from './ProjectBillingRateTemplate';
import ProjectNumber from './ProjectNumber';

const tabStyle = { paddingTop: 7, height: '90vh' };
const { TabPane } = Tabs;

function CustomizationSettings({
  activeKey,
  acPositions,
  positions,
  onCheckChanged,
  onOrderChanged,
}) {
  const { t } = useTranslation();
  const [stateKey, setStateKey] = useState(activeKey);
  const tabs = useSelector((state) => state.settings.tabs);
  const { paidFlags = [] } = useSelector((state) => state.settings.company ?? {});
  const hasCostTracking = paidFlags.includes('COST_TRACKING');

  const hasFilesTab = useMemo(() => tabs.some((tab) => tab.tabName === 'Files' && tab.enabled), [tabs]);
  const hasContractTab = useMemo(() => tabs.some((tab) => tab.tabName === 'Contract' && tab.enabled), [tabs]);
  const hasJobCostingTab = useMemo(() => tabs.some(
    (tab) => (tab.tabName === 'Cost Codes' || tab.tabName === 'Budget Tracking') && tab.enabled,
  ), [tabs]);

  useEffect(() => {
    setStateKey(activeKey);
  }, [activeKey]);

  const hasScheduleOfValuesWritePermission = Permissions.has('PROJECT_SCHEDULEOFVALUES_WRITE');
  const hasTranslationWritePermission = Permissions.has('TRANSLATIONS_WRITE');
  const hasProjectWritePermission = Permissions.has('PROJECTS_WRITE');
  const hasBucketsWritePermission = Permissions.has('BUCKETS_WRITE');
  const hasScheduleWritePermission = Permissions.has('SCHEDULE_WRITE');
  const hasCostcodeWritePermission = Permissions.has('COSTCODES_WRITE');

  return (
    <Row justify="start" style={{ height: '90%' }}>
      <Tabs
        defaultActiveKey={activeKey}
        tabPosition="left"
        tabBarStyle={{
          marginTop: 15,
          width: 135,
          marginRight: 17,
        }}
        style={{ width: '100%', height: '90vh' }}
        activeKey={stateKey}
        onChange={setStateKey}
      >
        <TabPane style={{ ...tabStyle, paddingTop: 0 }} tab="Tabs" key="tabsSettings">
          <TabsSettings />
        </TabPane>
        { hasJobCostingTab && (
          <TabPane style={tabStyle} tab="Job Costing" key="jobCosting">
            <JobCosting />
          </TabPane>
        )}
        <TabPane style={tabStyle} tab="Task Templates" key="taskTemplates">
          <Subtasks />
        </TabPane>
        { hasProjectWritePermission && (
          <TabPane style={tabStyle} tab="Project Rates" key="projectRates">
            <ProjectBillingRateTemplate />
          </TabPane>
        )}
        { hasProjectWritePermission && (
          <TabPane style={tabStyle} tab={`${t('Project')} Number`} key="projectNumbers">
            <ProjectNumber />
          </TabPane>
        )}
        { hasScheduleOfValuesWritePermission && hasContractTab && (
          <TabPane style={tabStyle} tab={`${t('Project')} Contract`} key="scheduleOfValues">
            <ScheduleOfValuesTemplates />
          </TabPane>
        )}
        <TabPane style={tabStyle} tab="Boards" key="boards">
          <Boards />
        </TabPane>
        { hasFilesTab && (
          <TabPane style={tabStyle} tab={`${t('Project')} Files`} key="projectTemplates">
            <ProjectFileTemplates />
          </TabPane>
        )}
        <TabPane style={tabStyle} tab="Types" key="types">
          <TypesTabView />
        </TabPane>
        { hasTranslationWritePermission && (
          <TabPane style={tabStyle} tab="Profile Names" key="profileNames">
            <ProfileNames />
          </TabPane>
        )}
        <TabPane style={tabStyle} tab="Custom Fields" key="customFields">
          <CustomFields />
        </TabPane>
        <TabPane style={tabStyle} tab="Action Center" key="actionCenter">
          <ActionCenter
            positions={positions}
            acPositions={acPositions}
            onCheckChanged={onCheckChanged}
            onOrderChanged={onOrderChanged}
          />
        </TabPane>
        { hasBucketsWritePermission && (
          <TabPane style={tabStyle} tab="Buckets" key="bucketTemplates">
            <BucketTemplateTabView />
          </TabPane>
        )}
        { hasScheduleWritePermission && (
          <TabPane style={tabStyle} tab="Schedule" key="schedule">
            <ScheduleDefaultDateTime />
            <ScheduleCategories />
          </TabPane>
        )}
        {hasCostcodeWritePermission && hasCostTracking && (
          <TabPane style={tabStyle} tab="Cost Codes" key="costCodes">
            <CostcodeCategories />
          </TabPane>
        )}
      </Tabs>
    </Row>
  );
}

CustomizationSettings.propTypes = {
  settings: PropTypes.shape({
  }),
  activeKey: PropTypes.string,
  acPositions: PropTypes.object,
  positions: PropTypes.object,
  onCheckChanged: PropTypes.func,
  onOrderChanged: PropTypes.func,

};
CustomizationSettings.defaultProps = {
  settings: {},
  activeKey: 'tabsSettings',
};

export default CustomizationSettings;
